import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import styled from "styled-components";
import { CrownOutlined, UserOutlined } from "@ant-design/icons";
import { PiBuildingsBold } from "react-icons/pi";
import { ProjectAPIs } from "~/common/typescript/apis/ProjectAPIs";
import { Project_Base } from "~/common/typescript/models/Project";
import { RoutesPrefix } from "~/routes/routes";
import { currentProjectAtom } from "~/stores/ProjectStore";
import { userACRoles } from "~/stores/UserStore";
import { AC_TLGeo_Role } from "~/common/typescript/services/access_control/AC_Tlgeo";
import { AC_Project, AC_Project_Role } from "~/common/typescript/services/access_control/AC_Project";
import defaultImage from './default_project_image.png';
import UserAvatar from "~/layouts/components/Header/components/UserAvatar/UserAvatar";
import { User_Base } from "~/common/typescript/models/User";

export type ProjectItemType = {
  project: Project_Base;
  role?: AC_Project_Role;
};

const ProjectItem = (props: ProjectItemType) => {
  const [, setCurrentProject] = useAtom(currentProjectAtom);
  const [roles] = useAtom(userACRoles);
  const isAdmin = roles.includes(AC_TLGeo_Role.TLGEO_ADMIN);
  const navigate = useNavigate();

  const [projectDetail, setProjectDetail] = useState<Project_Base | null>({
    ...props.project,
  });

  const fetchData = () => {
    const projectId = props.project.id;
    if (!projectId) return;
    ProjectAPIs.remote_getProjectById(projectId).then((project) => {
      setProjectDetail(project);
    });
  };

  useEffect(() => {
    fetchData();
  }, [props.project]);

  const projectToDisplay = projectDetail || props.project;
  const owner = typeof projectToDisplay?.owner === 'object' ? (projectToDisplay.owner as User_Base) : undefined;

  const ownerDisplayName = useMemo(() => {
    if (!owner) return 'Chưa cập nhật';
    const fullname = owner.fullname?.trim();
    const username = owner.username?.trim();

    if (isAdmin) {
      if (fullname && username && fullname !== username) {
        return `${fullname} (${username})`;
      }
      return username || fullname || 'Chưa cập nhật';
    }

    return fullname || username || 'Chưa cập nhật';
  }, [owner, isAdmin]);

  const orgName = useMemo(() => {
    if (projectToDisplay?.organization) {
      return projectToDisplay.organization.name || projectToDisplay.organization.shortName;
    }
    return null;
  }, [projectToDisplay?.organization]);

  const { i18n } = useTranslation();

  const roleName = useMemo(() => {
    if (!props.role) return null;
    if (typeof props.role === 'object' && props.role !== null) {
      const obj = props.role as any;
      const lang = (i18n?.language?.startsWith('en') ? 'en' : 'vi') as 'en' | 'vi';
      return obj[lang] || obj.vi || obj.en || '';
    }
    const roleObj = AC_Project.getRoleName(props.role);
    if (!roleObj) return null;
    if (typeof roleObj === 'string') return roleObj;
    const lang = (i18n?.language?.startsWith('en') ? 'en' : 'vi') as 'en' | 'vi';
    return roleObj[lang] || roleObj.vi || roleObj.en || '';
  }, [props.role, i18n?.language]);

  const handleClick = () => {
    setCurrentProject(projectToDisplay);
    navigate(`${RoutesPrefix.PROJECT}/${projectToDisplay.id}`);
  };

  const projectImage = projectToDisplay.avatar || projectToDisplay.image || defaultImage;

  return (
    <UI_Wrapper key={props.project.id} onClick={handleClick}>
      <UI_BGImage src={projectImage} alt="" />

      {roleName && (
        <UI_SharedBadge>{roleName}</UI_SharedBadge>
      )}

      <UI_Overlay>
        <UI_Name title={projectToDisplay.name}>{projectToDisplay.name}</UI_Name>
        {projectToDisplay.description && (
          <UI_Description title={projectToDisplay.description}>
            {projectToDisplay.description}
          </UI_Description>
        )}

        <UI_MetaRow>
          <UI_OwnerInfo title={`Chủ sở hữu: ${ownerDisplayName}`}>
            {owner ? (
              <UserAvatar user={owner} size={20} />
            ) : (
              <CrownOutlined style={{ color: '#fbbf24', fontSize: 13, flexShrink: 0 }} />
            )}
            <UI_OwnerName>{ownerDisplayName}</UI_OwnerName>
          </UI_OwnerInfo>

          {orgName ? (
            <UI_Tag title={`Doanh nghiệp: ${orgName}`}>
              <PiBuildingsBold style={{ fontSize: 12 }} />
              <span>{orgName}</span>
            </UI_Tag>
          ) : (
            <UI_Tag title="Dự án cá nhân">
              <UserOutlined style={{ fontSize: 11 }} />
              <span>Cá nhân</span>
            </UI_Tag>
          )}
        </UI_MetaRow>
      </UI_Overlay>
    </UI_Wrapper>
  );
};

export default memo(ProjectItem);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
`;

const UI_BGImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UI_SharedBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  background: rgba(37, 99, 235, 0.9);
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const UI_Overlay = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 10px 12px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0) 0%,
    rgba(15, 23, 42, 0.6) 25%,
    rgba(15, 23, 42, 0.92) 100%
  );
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-sizing: border-box;
  z-index: 1;
`;

const UI_Name = styled.h3`
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const UI_Description = styled.p`
  color: #cbd5e1;
  font-size: 12px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  line-height: 1.3;
`;

const UI_MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
  min-width: 0;
`;

const UI_OwnerInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const UI_OwnerName = styled.span`
  color: #f1f5f9;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const UI_Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  color: #e2e8f0;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
`;
